import {
  createContext,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { useSearchParams } from "react-router"
import { cn } from "@/primitives/utils"
import { useLadder, useRegistered } from "./page-chrome.tsx"
import { columnFit, paneOf, unseenOf, withPane } from "./pane-switch.ts"
import { SaveBarDockContext } from "./save-bar-dock.ts"
import { Segmented, type SegmentedOption } from "./segmented.tsx"
import { useViewport } from "./viewport.tsx"

/*
 * A task's work and the column beside it, as a change request's Spec, Ticket plan or Suggested
 * ticket sits beside its chat.
 *
 * On the desk they stand side by side, the column 352px wide and sticking 16px under the page
 * header as the work scrolls, never taller than the room left under it, and what follows them
 * (`after`) runs the page's width beneath both.
 *
 * On `compact` a phone can't hold both, so a full-width Segmented ("Spec | Chat") under the header
 * shows one at a time, sticking there as a 40px row of chrome (it registers as the page's toolbar,
 * so what sticks inside a pane sticks under it). Each choice's badge counts what waits behind it.
 * Both panes stay mounted, so an edit open in one and what was written in the other survive a
 * switch. The work shows with what follows it, and keeps its place: switching back returns to where
 * it was left. The column is as tall as the screen under the row, the page scrolled to it, down to
 * the docked bars when nothing but padding follows it, and scrolls what it holds itself, as a chat
 * does its list. A docked bar inside a pane (`usePaneShown`) docks only while its pane shows.
 *
 * Which pane shows is the page's to keep, in its address through `usePaneParam`, so a reload or a
 * shared link opens the same one.
 */

/** Whether the pane around shows now: always on the desk, and on `compact` while it is chosen. */
const PaneShownContext = createContext(true)

/** Whether the pane this sits in shows now, as a bar docked from it asks before it docks. */
export function usePaneShown() {
  return use(PaneShownContext)
}

/**
 * The pane the page's address shows (`?pane=`, the first pane without it), and what shows another,
 * replacing the address rather than adding to the history, so Back still leaves the page.
 */
export function usePaneParam<Pane extends string>([first, second]: readonly [Pane, Pane]) {
  const [search, setSearch] = useSearchParams()
  const shown = paneOf(search, [first, second])
  const show = useCallback(
    (pane: Pane) => setSearch((now) => withPane(now, pane, [first, second]), { replace: true }),
    [setSearch, first, second],
  )
  return [shown, show] as const
}

/**
 * How many of `arrived` (a running count, such as the agent's replies) came while they were out of
 * sight: everything counted while `inView` is seen, and so is the first count given, what was there
 * as the page loaded. Undefined while the page still loads, it counts nothing.
 */
export function useUnseen(arrived: number | undefined, inView: boolean) {
  const [seen, setSeen] = useState(arrived)
  useEffect(() => {
    if (inView || seen === undefined) setSeen(arrived)
  }, [arrived, inView, seen])
  return inView || arrived === undefined || seen === undefined ? 0 : unseenOf(arrived, seen)
}

const never = () => () => undefined

/** The padding and borders at the foot of what holds `pane`, up to and with `scroller`'s, in px. */
function paddingUnder(pane: HTMLElement, scroller: HTMLElement) {
  let padding = 0
  for (let holder = pane.parentElement; holder; holder = holder.parentElement) {
    const style = getComputedStyle(holder)
    padding += parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth)
    if (holder === scroller) break
  }
  return padding
}

/**
 * `panes` names the work and the column, in that order, each with its `badge`; `shown` is the one
 * chosen, and `onShow` chooses another. `children` is the work and `beside` the column. `label`
 * names the switch for a screen reader. On `compact` the row bleeds to the screen's edges over the
 * 16px gutters a body takes there.
 */
export function PaneSwitch<Pane extends string>({
  label,
  panes,
  shown,
  onShow,
  beside,
  after,
  children,
}: {
  label: string
  panes: readonly [SegmentedOption<Pane>, SegmentedOption<Pane>]
  shown: Pane
  onShow: (pane: Pane) => void
  beside: ReactNode
  after?: ReactNode
  children: ReactNode
}) {
  const { compact } = useViewport()
  const { underHeader } = useLadder()
  useRegistered("toolbar", compact)
  const row = useRef<HTMLDivElement>(null)
  const column = useRef<HTMLDivElement>(null)
  const workShown = !compact || shown === panes[0].value
  const columnShown = !compact || shown === panes[1].value
  // Where the work was left, to return to it there.
  const workPlace = useRef<number>(undefined)
  const dock = use(SaveBarDockContext)
  const docked = useSyncExternalStore(dock?.subscribe ?? never, () => dock?.height() ?? 0)

  // The column is as tall as the screen under the row: the page scroll's height less the stuck
  // chrome above it and whatever the page keeps after it. When that is only padding (the docked
  // bars', the body's), the column runs over it down to the docked bars (`columnFit`), and the page
  // stays scrolled to its end. A screen growing (the keyboard going down) clamps the page's scroll
  // before this runs, and with the column running to the dock the page has no slack to hide that,
  // so each fit puts the page back at its end, where arriving at the column put it.
  const fit = useCallback(() => {
    const pane = column.current
    const scroller = pane?.closest<HTMLElement>("[data-slot=page-scroll]")
    if (!pane || !row.current || !scroller) return
    const above = parseFloat(getComputedStyle(row.current).top) + row.current.offsetHeight
    const { height, top } = scroller.getBoundingClientRect()
    // Measured with the column a screen tall, so the page is long enough to say what follows it.
    pane.style.height = `${height}px`
    pane.style.marginBottom = ""
    const trailing =
      scroller.scrollHeight - (pane.getBoundingClientRect().bottom - top + scroller.scrollTop)
    const fitted = columnFit({
      screen: height,
      above,
      trailing,
      padding: paddingUnder(pane, scroller),
      docked: dock?.height() ?? 0,
    })
    pane.style.height = `${fitted.height}px`
    pane.style.marginBottom = fitted.overlap > 0 ? `${-fitted.overlap}px` : ""
    scroller.scrollTop = scroller.scrollHeight
  }, [dock])

  useLayoutEffect(() => {
    const pane = column.current
    if (!compact || !columnShown || !pane) return
    fit()
    const scroller = pane.closest<HTMLElement>("[data-slot=page-scroll]")
    if (!scroller) return
    const observer = new ResizeObserver(fit)
    observer.observe(scroller)
    return () => {
      observer.disconnect()
      pane.style.height = ""
      pane.style.marginBottom = ""
    }
  }, [compact, columnShown, docked, fit])

  // Arriving at the column scrolls the page to it; arriving back at the work returns to its place.
  useLayoutEffect(() => {
    if (!compact) return
    const scroller = row.current?.closest<HTMLElement>("[data-slot=page-scroll]")
    if (!scroller) return
    if (columnShown) scroller.scrollTop = scroller.scrollHeight
    else if (workPlace.current !== undefined) scroller.scrollTop = workPlace.current
  }, [compact, columnShown])

  const choose = (pane: Pane) => {
    if (pane === shown) return
    if (shown === panes[0].value)
      workPlace.current =
        row.current?.closest<HTMLElement>("[data-slot=page-scroll]")?.scrollTop ?? undefined
    onShow(pane)
  }

  return (
    <>
      {compact && (
        <div
          ref={row}
          data-slot="pane-switch"
          className={cn(
            "sticky z-20 -mx-4 flex h-10 items-center border-b border-border-subtle bg-background px-4",
            underHeader,
          )}
        >
          <Segmented label={label} value={shown} options={panes} onChange={choose} />
        </div>
      )}
      <div className="flex items-start gap-6 compact:flex-col compact:items-stretch">
        <PaneShownContext value={workShown}>
          <div className={cn("flex min-w-0 flex-1 flex-col gap-4", !workShown && "compact:hidden")}>
            {children}
          </div>
        </PaneShownContext>
        <PaneShownContext value={columnShown}>
          <div
            ref={column}
            className={cn(
              "sticky top-16 flex max-h-below-header-inset w-88 shrink-0 flex-col compact:static compact:max-h-none compact:w-full",
              // A pane is hidden only on `compact`, over the column's own `flex`.
              !columnShown && "compact:hidden",
            )}
          >
            {beside}
          </div>
        </PaneShownContext>
      </div>
      {after && (
        <PaneShownContext value={workShown}>
          <div className={cn("contents", !workShown && "compact:hidden")}>{after}</div>
        </PaneShownContext>
      )}
    </>
  )
}
